/**
 * Collection Performance Enhancements
 * Implements native lazy loading, predictive prefetching, and progressive enhancement
 */

(function() {
  'use strict';

  /**
   * Native Lazy Loading Enhancement
   * Upgrades images with data-src to use native srcset when in viewport
   */
  class NativeLazyLoader {
    constructor() {
      this.images = document.querySelectorAll('.lazyload-native[data-src]');
      this.imageObserver = null;
      this.init();
    }

    init() {
      // Check for native lazy loading support
      if ('loading' in HTMLImageElement.prototype) {
        // Native lazy loading is supported - upgrade images
        this.setupIntersectionObserver();
      } else {
        // Fallback for older browsers
        this.loadAllImages();
      }
    }

    setupIntersectionObserver() {
      const options = {
        root: null,
        rootMargin: '50px 0px', // Start loading 50px before entering viewport
        threshold: 0.01
      };

      this.imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.loadImage(entry.target);
            observer.unobserve(entry.target);
          }
        });
      }, options);

      this.images.forEach(img => this.imageObserver.observe(img));
    }

    loadImage(img) {
      if (img.dataset.src) {
        // Upgrade to full resolution
        img.src = img.dataset.src;

        // Upgrade srcset if available
        if (img.dataset.srcset) {
          img.srcset = img.dataset.srcset;
        }

        // Clean up data attributes
        delete img.dataset.src;
        delete img.dataset.srcset;

        // Add loaded class for CSS transitions
        img.classList.add('lazyloaded');
      }
    }

    loadAllImages() {
      // Fallback: Load all images immediately for browsers without IntersectionObserver
      this.images.forEach(img => this.loadImage(img));
    }
  }

  /**
   * Predictive Prefetching for Pagination
   * Prefetches next page when user scrolls near bottom
   */
  class PaginationPrefetcher {
    constructor() {
      this.nextPageLink = document.querySelector('.pagination__next');
      this.prefetched = false;
      this.init();
    }

    init() {
      if (!this.nextPageLink) return;

      // Create an observer for the last row of products
      const lastProducts = document.querySelectorAll('.product-item:nth-last-child(-n+4)');
      if (lastProducts.length === 0) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.prefetched) {
            this.prefetchNextPage();
          }
        });
      }, {
        rootMargin: '100px'
      });

      lastProducts.forEach(product => observer.observe(product));
    }

    prefetchNextPage() {
      if (this.prefetched) return;

      const nextPageUrl = this.nextPageLink.href;
      if (!nextPageUrl) return;

      // Prefetch the HTML
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = nextPageUrl;
      link.as = 'document';
      document.head.appendChild(link);

      // Also prefetch as low priority fetch for better caching
      if ('fetch' in window) {
        fetch(nextPageUrl, {
          method: 'GET',
          mode: 'no-cors',
          priority: 'low'
        }).catch(() => {}); // Silently fail
      }

      this.prefetched = true;
    }
  }

  /**
   * Resource Hints for Critical Resources
   * Adds preconnect and dns-prefetch for external resources
   */
  class ResourceHints {
    constructor() {
      this.init();
    }

    init() {
      const hints = [
        { rel: 'preconnect', href: 'https://cdn.shopify.com' },
        { rel: 'dns-prefetch', href: 'https://cdn.shopify.com' },
        { rel: 'preconnect', href: 'https://fonts.shopifycdn.com' },
        { rel: 'dns-prefetch', href: 'https://fonts.shopifycdn.com' }
      ];

      hints.forEach(hint => {
        if (!document.querySelector(`link[rel="${hint.rel}"][href="${hint.href}"]`)) {
          const link = document.createElement('link');
          link.rel = hint.rel;
          link.href = hint.href;
          if (hint.rel === 'preconnect') {
            link.crossOrigin = 'anonymous';
          }
          document.head.appendChild(link);
        }
      });
    }
  }

  /**
   * Filter Performance Optimisation
   * Lazy loads filter content when needed
   */
  class FilterOptimizer {
    constructor() {
      this.filterButton = document.querySelector('[data-action="open-drawer"]');
      this.filtersLoaded = false;
      this.init();
    }

    init() {
      if (!this.filterButton) return;

      // Load filters on first interaction
      this.filterButton.addEventListener('click', () => {
        if (!this.filtersLoaded) {
          this.loadFilters();
        }
      }, { once: true });

      // Preload on hover (desktop)
      this.filterButton.addEventListener('mouseenter', () => {
        if (!this.filtersLoaded) {
          this.loadFilters();
        }
      }, { once: true });
    }

    loadFilters() {
      // Trigger any deferred filter loading here
      const filterDrawer = document.querySelector('#mobile-collection-filters');
      if (filterDrawer && filterDrawer.dataset.deferred) {
        // Load deferred content
        delete filterDrawer.dataset.deferred;
      }
      this.filtersLoaded = true;
    }
  }

  /**
   * Out of Stock Product Sorter
   * Moves out-of-stock products to end of collection using native DOM manipulation
   */
  class OutOfStockSorter {
    constructor() {
      this.productGrid = document.querySelector('.product-list--collection');
      this.init();
    }

    init() {
      if (!this.productGrid) return;

      // Wait for initial render
      requestAnimationFrame(() => {
        this.sortProducts();
      });
    }

    sortProducts() {
      const products = Array.from(this.productGrid.querySelectorAll('.product-item'));
      if (products.length === 0) return;

      // Separate in-stock and out-of-stock products
      const inStock = [];
      const outOfStock = [];

      products.forEach(product => {
        // Check for sold out badge or variant availability
        const isSoldOut = product.querySelector('.product-item__badge--sold-out') ||
                          product.classList.contains('product-item--sold-out') ||
                          product.querySelector('[data-available="false"]');

        if (isSoldOut) {
          outOfStock.push(product);
        } else {
          inStock.push(product);
        }
      });

      // Only re-sort if there are out-of-stock products
      if (outOfStock.length === 0) return;

      // Create document fragment for efficient DOM manipulation
      const fragment = document.createDocumentFragment();

      // Append in-stock products first
      inStock.forEach(product => fragment.appendChild(product));

      // Append out-of-stock products last
      outOfStock.forEach(product => fragment.appendChild(product));

      // Single DOM update
      this.productGrid.appendChild(fragment);
    }
  }

  /**
   * Performance Monitoring
   * Tracks Core Web Vitals and sends to analytics
   */
  class PerformanceMonitor {
    constructor() {
      this.init();
    }

    init() {
      // Monitor Largest Contentful Paint
      if ('PerformanceObserver' in window) {
        try {
          const lcpObserver = new PerformanceObserver((list) => {
            const entries = list.getEntries();
            const lastEntry = entries[entries.length - 1];
            console.log('LCP:', lastEntry.renderTime || lastEntry.loadTime);
          });
          lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
        } catch (e) {
          // LCP observer not supported
        }
      }

      // Monitor First Input Delay
      if ('PerformanceObserver' in window) {
        try {
          const fidObserver = new PerformanceObserver((list) => {
            const entries = list.getEntries();
            entries.forEach(entry => {
              console.log('FID:', entry.processingStart - entry.startTime);
            });
          });
          fidObserver.observe({ type: 'first-input', buffered: true });
        } catch (e) {
          // FID observer not supported
        }
      }

      // Monitor Cumulative Layout Shift
      if ('PerformanceObserver' in window) {
        try {
          let clsScore = 0;
          const clsObserver = new PerformanceObserver((list) => {
            for (const entry of list.getEntries()) {
              if (!entry.hadRecentInput) {
                clsScore += entry.value;
              }
            }
            console.log('CLS:', clsScore);
          });
          clsObserver.observe({ type: 'layout-shift', buffered: true });
        } catch (e) {
          // CLS observer not supported
        }
      }
    }
  }

  /**
   * Initialize all performance enhancements
   */
  function initPerformanceEnhancements() {
    // Wait for DOM ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }

    function init() {
      // Initialize modules
      new NativeLazyLoader();
      new PaginationPrefetcher();
      new ResourceHints();
      new FilterOptimizer();
      new OutOfStockSorter();

      // Only monitor performance in production
      if (!window.location.hostname.includes('localhost')) {
        new PerformanceMonitor();
      }
    }
  }

  // Start initialization
  initPerformanceEnhancements();

  /**
   * Export for use in other scripts
   */
  window.CollectionPerformance = {
    NativeLazyLoader,
    PaginationPrefetcher,
    ResourceHints,
    FilterOptimizer,
    OutOfStockSorter,
    PerformanceMonitor
  };
})();